import { useState } from 'react';
import { userApi } from '../../api/client';
import { useApiQuery } from '../../hooks/useApiQuery';
import { Card, CardBody, CardHeader, Badge, Button, ErrorAlert } from '../../components/ui';
import { TRANSACTION_TYPE } from '../../lib/constants';
import { Coins, TrendingUp, TrendingDown, ChevronLeft, ChevronRight } from 'lucide-react';

// Helper to get date 1 year ago in YYYY-MM-DD format
const getOneYearAgo = () => {
  const date = new Date();
  date.setFullYear(date.getFullYear() - 1);
  return date.toISOString().split('T')[0];
};

// Helper to get today's date in YYYY-MM-DD format
const getToday = () => {
  return new Date().toISOString().split('T')[0];
};

export function CreditsPage() {
  const [page, setPage] = useState(1);
  const [startDate, setStartDate] = useState(getOneYearAgo());
  const [endDate, setEndDate] = useState(getToday());

  const { data, isLoading, isError, error, refetch } = useApiQuery({
    queryKey: ['credits', page, startDate, endDate],
    queryFn: () => userApi.getCredits(page, 20, startDate, endDate),
    refetchOnMount: 'always',
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Credit History</h1>
        <div className="flex items-center gap-4 text-sm">
          <div className="flex items-center gap-2">
            <label className="text-gray-600">From:</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setPage(1);
              }}
              className="input py-1 px-2 text-sm"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-gray-600">To:</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setPage(1);
              }}
              className="input py-1 px-2 text-sm"
            />
          </div>
        </div>
      </div>

      {isError && (
        <ErrorAlert message={error?.message} onRetry={() => refetch()} />
      )}

      {/* Balance Card */}
      <Card>
        <CardBody className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="p-4 bg-primary-100 rounded-xl">
              <Coins className="w-8 h-8 text-primary-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Current Balance</p>
              <p className="text-4xl font-bold text-gray-900">
                {data?.balance || 0}
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500">credits available</p>
          </div>
        </CardBody>
      </Card>

      {/* Transactions */}
      <Card>
        <CardHeader>
          <h2 className="text-lg font-semibold text-gray-900">Transaction History</h2>
        </CardHeader>
        <CardBody className="!p-0">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : data?.transactions.length === 0 ? (
            <p className="p-6 text-gray-500 text-center">No transactions yet</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {data?.transactions.map((transaction) => (
                    <tr key={transaction.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(transaction.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Badge
                          variant={
                            transaction.type === 'reward'
                              ? 'green'
                              : transaction.type === 'payment'
                              ? 'red'
                              : 'blue'
                          }
                        >
                          {TRANSACTION_TYPE[transaction.type]?.label}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {transaction.description}
                        {transaction.task_name && (
                          <span className="text-gray-500"> - {transaction.task_name}</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <span
                          className={`font-semibold flex items-center justify-end ${
                            transaction.amount > 0 ? 'text-green-600' : 'text-red-600'
                          }`}
                        >
                          {transaction.amount > 0 ? (
                            <TrendingUp className="w-4 h-4 mr-1" />
                          ) : (
                            <TrendingDown className="w-4 h-4 mr-1" />
                          )}
                          {transaction.amount > 0 ? '+' : ''}
                          {transaction.amount}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {data?.pagination && data.pagination.pages > 1 && (
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Showing {(page - 1) * 20 + 1} to{' '}
                {Math.min(page * 20, data.pagination.total)} of{' '}
                {data.pagination.total} transactions
              </p>
              <div className="flex space-x-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setPage(page + 1)}
                  disabled={page >= data.pagination.pages}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
